import { useEffect, useState } from 'react';
import { Download, RotateCcw, Save } from 'lucide-react';
import { Alert, Button, Card, Field, PageHeader, Spinner } from '../../components/ui';
import { useToast } from '../../components/Toast';
import { ApiError } from '../../api/client';
import { downloadCsv, useSettings, useUpdateSettings } from '../../api/queries';
import { ZONE_COLORS } from '../../lib/zone';

const DEFAULTS = { risk_threshold: 75, yellow_threshold: 65, red_threshold: 51, streak_risk_min: 3 };

export function SettingsPage() {
  const toast = useToast();
  const { data: settings, isLoading } = useSettings();
  const save = useUpdateSettings();

  const [risk, setRisk] = useState('75');
  const [yellow, setYellow] = useState('65');
  const [red, setRed] = useState('51');
  const [streak, setStreak] = useState('3');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busyExport, setBusyExport] = useState<string | null>(null);

  useEffect(() => {
    if (!settings) return;
    setRisk(String(settings.risk_threshold));
    setYellow(String(settings.yellow_threshold));
    setRed(String(settings.red_threshold));
    setStreak(String(settings.streak_risk_min));
  }, [settings]);

  async function submit() {
    setErrors({});
    try {
      await save.mutateAsync({
        risk_threshold: Number(risk),
        yellow_threshold: Number(yellow),
        red_threshold: Number(red),
        streak_risk_min: Number(streak),
      });
      toast.success('პარამეტრები შენახულია — დეშბორდი გადაითვალა');
    } catch (err) {
      if (err instanceof ApiError) {
        setErrors(err.details ?? {});
        if (!err.details) toast.error(err.message);
      } else {
        toast.error('შენახვა ვერ მოხერხდა');
      }
    }
  }

  function restoreDefaults() {
    setRisk(String(DEFAULTS.risk_threshold));
    setYellow(String(DEFAULTS.yellow_threshold));
    setRed(String(DEFAULTS.red_threshold));
    setStreak(String(DEFAULTS.streak_risk_min));
  }

  async function exportCsv(path: string) {
    setBusyExport(path);
    try {
      await downloadCsv(path);
      toast.success('ფაილი ჩამოიტვირთა');
    } catch {
      toast.error('ექსპორტი ვერ მოხერხდა');
    } finally {
      setBusyExport(null);
    }
  }

  if (isLoading) {
    return (
      <>
        <PageHeader title="პარამეტრები" />
        <div className="flex justify-center py-16">
          <Spinner className="size-6" />
        </div>
      </>
    );
  }

  const redN = Number(red);
  const yellowN = Number(yellow);
  const riskN = Number(risk);

  return (
    <>
      <PageHeader
        title="პარამეტრები"
        subtitle="რისკის ზღვრები მოქმედებს დეშბორდზე, გაცდენების დაფაზე და ყველა ფერზე"
      />

      <div className="grid gap-5 lg:grid-cols-2">
        <Card title="რისკის ზღვრები">
          <div className="space-y-4">
            <Field
              label="რისკის ზღვარი (%)"
              type="number"
              min={0}
              max={100}
              value={risk}
              onChange={(e) => setRisk(e.target.value)}
              error={errors.risk_threshold}
              hint="ამ ზღვარს ქვემოთ სტუდენტი რისკშია და გაცდენების დაფაზე ჩნდება"
            />
            <Field
              label="ყვითელი ზონა (%)"
              type="number"
              min={0}
              max={100}
              value={yellow}
              onChange={(e) => setYellow(e.target.value)}
              error={errors.yellow_threshold}
            />
            <Field
              label="წითელი ზონა (%)"
              type="number"
              min={0}
              max={100}
              value={red}
              onChange={(e) => setRed(e.target.value)}
              error={errors.red_threshold}
              hint="ამ ზღვარს ქვემოთ — წითელი; ყვითელზე მეტი ვერ იქნება"
            />
            <Field
              label="ზედიზედ გაცდენა = რისკი"
              type="number"
              min={1}
              max={20}
              value={streak}
              onChange={(e) => setStreak(e.target.value)}
              error={errors.streak_risk_min}
              hint="პროცენტის მიუხედავად — ამდენი ზედიზედ გაცდენა რისკის სიგნალია"
            />

            {/* --- ვიზუალური მასშტაბი --- */}
            <div>
              <p className="mb-1.5 text-[12px] font-medium text-ink-soft">ფერითი ზონები</p>
              <div className="flex h-7 overflow-hidden rounded-lg text-[10px] font-medium text-white">
                <div
                  className="grid place-items-center"
                  style={{ width: `${Math.max(redN, 0)}%`, background: ZONE_COLORS.red }}
                >
                  {redN > 12 && `0–${redN}%`}
                </div>
                <div
                  className="grid place-items-center"
                  style={{
                    width: `${Math.max(riskN - redN, 0)}%`,
                    background: ZONE_COLORS.yellow,
                  }}
                >
                  {riskN - redN > 12 && `${redN}–${riskN}%`}
                </div>
                <div
                  className="grid place-items-center"
                  style={{ width: `${Math.max(100 - riskN, 0)}%`, background: ZONE_COLORS.green }}
                >
                  {100 - riskN > 12 && `${riskN}–100%`}
                </div>
              </div>
              {redN > yellowN && (
                <p className="mt-1.5 text-[12px] text-danger">
                  წითელი ზღვარი ყვითელზე მეტია — შენახვა დაიბლოკება.
                </p>
              )}
            </div>

            <div className="flex gap-2 pt-1">
              <Button icon={<Save className="size-4" />} onClick={() => void submit()} loading={save.isPending}>
                შენახვა
              </Button>
              <Button
                variant="secondary"
                icon={<RotateCcw className="size-4" />}
                onClick={restoreDefaults}
              >
                ნაგულისხმევი
              </Button>
            </div>
          </div>
        </Card>

        <Card title="მონაცემების ექსპორტი">
          <Alert kind="info">
            ფაილები UTF-8 BOM-ით ინახება — Excel ქართულს სწორად წაიკითხავს.
          </Alert>

          <ul className="mt-4 space-y-2">
            {[
              {
                path: 'students',
                title: 'სტუდენტები და დასწრება',
                desc: 'ყველა ჩარიცხვა, გამოთვლილი %, streak, რისკის სტატუსი',
              },
              {
                path: 'groups',
                title: 'ჯგუფები',
                desc: 'განრიგი, ლექტორი, სტუდენტების რაოდენობა, საშუალო დასწრება',
              },
              {
                path: 'attendance',
                title: 'დასწრების ჟურნალი',
                desc: 'თითოეული გაკვეთილი, თემა და მონიშვნა — დეტალურად',
              },
            ].map((item) => (
              <li
                key={item.path}
                className="flex items-center gap-3 rounded-lg border border-line px-3.5 py-2.5"
              >
                <span className="min-w-0 flex-1">
                  <span className="block text-[13px] font-medium text-ink">{item.title}</span>
                  <span className="block text-[12px] text-ink-mute">{item.desc}</span>
                </span>
                <Button
                  size="sm"
                  variant="secondary"
                  icon={<Download className="size-4" />}
                  loading={busyExport === item.path}
                  onClick={() => void exportCsv(item.path)}
                >
                  CSV
                </Button>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </>
  );
}
